import { Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'src/utils/store'
import { useDispatch } from 'react-redux'
import { actions } from '../../../RestockingState'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import { useBlockPopAction } from 'src/hooks/useBlockPopAction'
import { ItemToRestock } from 'src/features/restocking/RestockingTypes'
import Page from 'src/features/restocking/components/RestockingWebAppForm/components/Page/Page'
import ScanCode from 'src/features/restocking/components/RestockingWebAppForm/components/ScanCode/ScanCode'
import TextLabel from 'src/features/restocking/components/RestockingWebAppForm/components/TextLabel/TextLabel'
import UserGuidance from 'src/features/restocking/components/RestockingWebAppForm/components/UserGuidance/UserGuidance'
import UserIdentifier from 'src/features/restocking/components/RestockingWebAppForm/components/UserIdentifier/UserIdentifier'
import theme from 'src/styles/theme'
import RestockingConfirmationModal from 'src/features/restocking/components/RestockingWebAppForm/components/RestockingConfirmationModal/RestockingConfirmationModal'
import RestockingBatchesTable from 'src/features/restocking/components/RestockingWebAppForm/components/RestockingBatchesTable/RestockingBatchesTable'
import ModalQuantityUnfitInShelf from 'src/features/restocking/components/RestockingWebAppForm/components/ModalQuantityUnfitInShelf/ModalQuantityUnfitInShelf'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import CardRestockProduct from 'src/features/restocking/components/RestockingWebAppForm/components/CardRestockProduct/CardRestockProduct'
import * as S from './RestockProductPage.styles'

interface FormData {
  barcode: string
  unfitQuantity: number | undefined
}

const RestockProductPage: React.FC = () => {
  const {
    currentProductToRestock, user, restockingConfig,
  } = useSelector((state) => state.restocking)

  const [barcodeIsValid, setBarcodeIsValid] = useState(restockingConfig?.dropOff)
  const [showRestockingConfirmationModal, setShowRestockingConfirmationModal] = useState(false)
  const [showModalToConfirmAllProductsRestock, setShowModalToConfirmAllProductsRestock] = useState(false)
  const [showUnfitQuantityModal, setShowUnfitQuantityModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const { fc, validateFc } = useFulfillmentCenter()
  const {
    register,
    getValues,
    setValue,
    watch,
    clearErrors,
    setError,
    reset,
    formState: { errors },
  } = useForm<FormData>()
  const history = useHistory()
  const dispatch = useDispatch()

  useEffect(() => {
    validateFc()
  }, [validateFc])

  useEffect(() => {
    if (!currentProductToRestock) {
      const productToRestock = user.itemsToRestock?.find((item: ItemToRestock) => !item.done)

      if (productToRestock) {
        dispatch(actions.setProperty({
          property: 'currentProductToRestock',
          value: productToRestock,
        }))
      }
    }
  }, [dispatch, currentProductToRestock, user.itemsToRestock])

  useBlockPopAction()

  const watchUnfitQuantity = watch('unfitQuantity')

  const restockAddress = currentProductToRestock?.location.address

  const closeRestockingConfirmationModal = () => setShowRestockingConfirmationModal(false)
  const closeRestockingAllConfirmationModal = () => setShowModalToConfirmAllProductsRestock(false)

  const onSubmitProductRestock = () => {
    if (!currentProductToRestock) return

    dispatch(actions.finishRestockingAddressRestock({
      badge: user.badge,
      idItem: currentProductToRestock.idPageItem,
      idModel: currentProductToRestock.idModel,
      history,
      onSuccess: closeRestockingConfirmationModal,
    }))
  }

  const onSubmitAllProductsRestock = () => {
    if (!user.itemsToRestock) return

    dispatch(actions.finishRestockingAddressRestock({
      badge: user.badge,
      itemsToFinish: user.itemsToRestock,
      history,
      onSuccess: closeRestockingConfirmationModal,
    }))
  }

  const closeUnfitQuantityModal = () => {
    setShowUnfitQuantityModal(false)
    clearErrors('unfitQuantity')
    setValue('unfitQuantity', undefined)
  }

  useEffect(() => {
    if (!currentProductToRestock) return

    if (watchUnfitQuantity) {
      if (watchUnfitQuantity <= 0) {
        setError('unfitQuantity', { message: 'Quantidade deve ser maior que zero!' })
        return
      }

      const { totalToRestock } = currentProductToRestock

      if (watchUnfitQuantity > totalToRestock) {
        setError('unfitQuantity', { message: `Quantidade deve ser menor ou igual a ${totalToRestock}!` })
        return
      }

      if (!new RegExp('^[0-9]*$').test(watchUnfitQuantity.toString())) {
        setError('unfitQuantity', { message: 'Quantidade deve ser um número inteiro!' })
        return
      }

      clearErrors()
    }
  }, [currentProductToRestock, watchUnfitQuantity, setError, clearErrors])

  const onSubmitUnfitShelf = () => {
    if (!currentProductToRestock || (!watchUnfitQuantity && currentProductToRestock.location.isGondola)) return

    const {
      idPage, idPageItem, totalToRestock,
    } = currentProductToRestock

    dispatch(actions.finishRestockingAddressRestockUnfit({
      badge: user.badge,
      idFulfillmentCenter: fc.id,
      idPage,
      quantity: Number(watchUnfitQuantity) || totalToRestock,
      idItem: idPageItem,
      history,
    }))

    closeUnfitQuantityModal()
  }

  const onSuccess = () => setBarcodeIsValid(true)

  const validateBarcode = () => {
    if (!currentProductToRestock) return

    const barcode = getValues('barcode').trim()

    dispatch(actions.restockingValidateProduct({
      badge: user.badge,
      idModel: currentProductToRestock.idModel,
      productBarcode: barcode,
      onSuccess,
    }))

    reset({ barcode: '' })
  }

  const formatQuantity = (qty: number) => `${qty} ${qty > 1 ? 'unidades' : 'unidade'}`

  const handleFinishRestockingAction = () => {
    if (restockingConfig?.dropOff) {
      setShowModalToConfirmAllProductsRestock(true)
    } else {
      setShowRestockingConfirmationModal(true)
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)

  const filteredItemsToRestock = user.itemsToRestock
    ?.filter((item: ItemToRestock) => item.description.toLowerCase().includes(searchTerm.toLowerCase()))
    .map((item: ItemToRestock) => (
      <CardRestockProduct key={item.idModel} product={item} />
    ))

  return (
    <Page headerTitle='Reposição' hasBackButton={false} isRestocking>
      <S.Info>
        <UserIdentifier idPage={currentProductToRestock?.idPage}/>
        <UserGuidance>
          <TextLabel
            width='100%'
            height='3.2rem'
            backgroundColor={theme.colors.green4}
          >
            <Typography style={{ fontWeight: 'bold', fontSize: '30px' }}>
              {restockAddress}
            </Typography>
          </TextLabel>
        </UserGuidance>
        <S.ContainerProductName>
          <Typography style={{ fontWeight: 'bold', fontSize: '36px' }}>REPONHA</Typography>
          {
            !restockingConfig?.dropOff && (
              <UserGuidance>
                <TextLabel
                  width='100%'
                  height='3.2rem'
                  backgroundColor={barcodeIsValid ? theme.colors.green4 : undefined}
                >
                  <Typography style={{ fontWeight: 'bold', fontSize: '13px' }}>
                    {currentProductToRestock?.description}
                  </Typography>
                </TextLabel>
              </UserGuidance>
            )
          }
          {
            barcodeIsValid && currentProductToRestock?.totalToRestock && (
              <>
                {
                  restockingConfig?.dropOff ? (
                    <>
                      <S.CustomTextField
                        id='productDescription'
                        name='productDescription'
                        type='text'
                        autoComplete='off'
                        value={searchTerm}
                        label='Buscar por nome do produto'
                        testId='search-product-test-id'
                        fullWidth
                        onChange={handleSearchChange}
                      />
                      <S.ContainerRestockCards>
                        {
                          filteredItemsToRestock.length > 0 ? filteredItemsToRestock : (
                            <Typography textAlign='center'>
                              Nenhum produto encontrado
                            </Typography>
                          )
                        }
                      </S.ContainerRestockCards>
                    </>
                  ) : (
                    <>
                      <Typography pt={2} style={{ fontWeight: 'bold', fontSize: '36px' }}>
                        {formatQuantity(currentProductToRestock?.totalToRestock)}
                      </Typography>
                      <RestockingBatchesTable
                        batches={currentProductToRestock.batches}
                        color={theme.colors.black}
                      />
                    </>
                  )
                }
              </>
            )
          }
        </S.ContainerProductName>
      </S.Info>
      <S.Form>
        {
            barcodeIsValid ? (
              <S.ContainerButton>
                {restockingConfig?.hasUnfit && (
                <S.CustomButton
                  title='Não coube'
                  bgColor='orange2'
                  hoverColor='orange'
                  letterColor='white'
                  data-testid='didnt-fit-button-test-id'
                  onClick={() => setShowUnfitQuantityModal(true)}
                />
                )}
                <S.CustomButton
                  title={restockingConfig?.dropOff ? 'Finalizar reposição' : 'Coube tudo'}
                  bgColor='confirm'
                  hoverColor='confirmHover'
                  letterColor='white'
                  data-testid='fit-all-button-test-id'
                  onClick={handleFinishRestockingAction}
                />
              </S.ContainerButton>
            ) : (
              <ScanCode
                text='Ao localizar o produto bipe o código de barras.'
                onSubmit={validateBarcode}
                register={register}
                dataTestId='input-barcode-test-id'
                inputName='barcode'
                errorMessage='Código de barras inválido!'
              />
            )
          }
      </S.Form>
      {
        currentProductToRestock?.location.isGondola ? (
          <ModalQuantityUnfitInShelf
            show={showUnfitQuantityModal}
            title='Não coube - gôndola'
            inputName='unfitQuantity'
            quantityToRestock={currentProductToRestock.totalToRestock}
            unfitQuantity={watchUnfitQuantity}
            register={register}
            errorMessage={errors.unfitQuantity?.message}
            errors={errors.unfitQuantity}
            onConfirm={onSubmitUnfitShelf}
            onReject={closeUnfitQuantityModal}
          />
        ) : (
          <ConfirmOrRejectModal
            show={showUnfitQuantityModal}
            title='Não coube - excedente'
            customizableDescription={(
              <S.ContainerDescription>
                <p>
                  Ao confirmar a ação, <strong>TODAS</strong> as unidades deverão ser movidas para uma nova posição.
                </p>
                <p>
                  Deseja prosseguir?
                </p>
              </S.ContainerDescription>
          )}
            onConfirm={onSubmitUnfitShelf}
            onReject={closeUnfitQuantityModal}
            onClose={closeUnfitQuantityModal}
            dataTestId='unfit-quantity-modal-test-id'
          />
        )
      }
      <RestockingConfirmationModal
        show={showRestockingConfirmationModal}
        title='Finalizar reposição'
        onConfirm={onSubmitProductRestock}
        onReject={closeRestockingConfirmationModal}
        dataTestId='restocking-confirmation-modal-test-id'
      />
      <ConfirmOrRejectModal
        show={showModalToConfirmAllProductsRestock}
        title='Finalizar reposição'
        customizableDescription={(
          <S.ContainerDescription>
            <p>
              Ao confirmar a ação, <strong>TODOS</strong> os produtos serão movidos.
            </p>
            <p>
              Deseja prosseguir?
            </p>
          </S.ContainerDescription>
          )}
        onConfirm={onSubmitAllProductsRestock}
        onReject={closeRestockingAllConfirmationModal}
        onClose={closeRestockingAllConfirmationModal}
        dataTestId='restocking-all-confirmation-modal-test-id'
      />
    </Page>
  )
}

export default RestockProductPage
